import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, User, Key, Camera, Wallet, Check, ChevronRight,
  X, Rocket, ArrowRight, PartyPopper
} from 'lucide-react';
import { partnerAPI } from '../api';

const OnboardingWizard = ({ onDismiss }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    fetchOnboarding();
  }, []);

  const fetchOnboarding = async () => {
    try {
      const response = await partnerAPI.getOnboarding();
      setOnboarding(response.data);
      
      // Show celebration if just completed
      if (response.data.is_complete && !response.data.is_dismissed) {
        setShowCelebration(true);
      }
    } catch (error) {
      console.error('Failed to load onboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    try {
      await partnerAPI.dismissOnboarding();
      onDismiss?.();
    } catch (error) {
      console.error('Failed to dismiss:', error);
    }
  };

  const handleStepClick = (route) => {
    navigate(route);
    setMinimized(true);
  };

  const getStepIcon = (stepKey) => {
    const icons = {
      profile: User,
      api_key: Key,
      first_tryon: Camera,
      wallet: Wallet
    };
    return icons[stepKey] || Sparkles;
  };

  if (loading || !onboarding?.show_onboarding) {
    return null;
  }

  const { steps, completed_count, total_steps, progress_percent, is_complete } = onboarding;
  const stepOrder = ['profile', 'api_key', 'first_tryon', 'wallet'];

  // Celebration Modal
  if (showCelebration) {
    return (
      <AnimatePresence>
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 10, delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center"
            >
              <PartyPopper className="w-10 h-10 text-white" />
            </motion.div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">You're All Set! 🎉</h2>
            <p className="text-slate-600 mb-6">
              Congratulations! You've completed the onboarding. Your account is ready to use.
            </p>
            
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">100</p>
                <p className="text-xs text-slate-500">Free Usage Balance</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">1</p>
                <p className="text-xs text-slate-500">API Key Ready</p>
              </div>
            </div>
            
            <button
              onClick={() => { setShowCelebration(false); handleDismiss(); }}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              Start Building <Rocket className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  }

  // Minimized View
  if (minimized) {
    return (
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-4 right-4 z-40"
      >
        <button
          onClick={() => setMinimized(false)}
          className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-shadow"
        >
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-blue-600" />
            </div>
            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">
              {total_steps - completed_count}
            </div>
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-slate-900">Continue Setup</p>
            <p className="text-xs text-slate-500">{completed_count}/{total_steps} completed</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </button>
      </motion.div>
    );
  }

  // Full Onboarding Panel
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-4 right-4 z-40 w-96"
      data-testid="onboarding-wizard"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Rocket className="w-5 h-5" />
              <h3 className="font-semibold">Getting Started</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMinimized(true)}
                className="p-1 hover:bg-white/20 rounded transition-colors text-xs"
              >
                Minimize
              </button>
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress_percent}%` }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="h-full bg-white rounded-full"
            />
          </div>
          <p className="text-sm text-white/80 mt-2">{completed_count} of {total_steps} steps completed</p>
        </div>

        {/* Steps */}
        <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
          {stepOrder.map((stepKey, index) => {
            const step = steps[stepKey];
            const Icon = getStepIcon(stepKey);
            const isNext = !step.completed && stepOrder.slice(0, index).every(k => steps[k].completed);
            
            return (
              <motion.button
                key={stepKey}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => !step.completed && handleStepClick(step.route)}
                disabled={step.completed}
                className={`w-full p-3 rounded-xl flex items-center gap-3 text-left transition-all ${
                  step.completed
                    ? 'bg-green-50 cursor-default'
                    : isNext
                      ? 'bg-blue-50 hover:bg-blue-100 cursor-pointer border-2 border-blue-200'
                      : 'bg-slate-50 hover:bg-slate-100 cursor-pointer'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  step.completed
                    ? 'bg-green-500'
                    : isNext
                      ? 'bg-blue-500'
                      : 'bg-slate-300'
                }`}>
                  {step.completed ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <Icon className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm ${step.completed ? 'text-green-700' : 'text-slate-900'}`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{step.description}</p>
                </div>
                {!step.completed && (
                  <ArrowRight className={`w-4 h-4 flex-shrink-0 ${isNext ? 'text-blue-500' : 'text-slate-400'}`} />
                )}
              </motion.button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50">
          <p className="text-xs text-slate-500 text-center">
            Complete these steps to get the most out of Nanokart.ai
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default OnboardingWizard;
